import { type FormEvent, useState } from 'react'
import { FlaskConical, LoaderCircle, ShieldCheck, Trash2, UserPlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useGrantInternalModelEntitlement,
  useInternalModelEntitlements,
  useRevokeInternalModelEntitlement,
} from '../api/hooks'
import AdminToolNav from '../components/admin/AdminToolNav'
import { createNadDebug } from '../utils/debugLog'

const log = createNadDebug('dw:internal-model-entitlement-admin:operator-ui')

function errorMessage(error: object | null | undefined, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback
}

export default function AdminInternalModelsPage() {
  const { t } = useTranslation('pages')
  const [email, setEmail] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const entitlements = useInternalModelEntitlements()
  const grant = useGrantInternalModelEntitlement()
  const revoke = useRevokeInternalModelEntitlement()
  const accounts = entitlements.data?.entitlements ?? []

  async function handleGrant(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setNotice(null)
    const target = email.trim().toLowerCase()
    try {
      const granted = await grant.mutateAsync(target)
      setEmail('')
      setNotice(t('adminInternalModels.granted', { email: granted.email }))
      log({
        event: 'internal-model-entitlement-admin:grant-succeeded',
        feature: 'internal-model-entitlement-admin',
        concern: 'operator-ui',
        target_user_id: granted.user_id,
      })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('adminInternalModels.grantError'))
    }
  }

  async function handleRevoke(userId: string, targetEmail: string) {
    setError(null)
    setNotice(null)
    try {
      await revoke.mutateAsync(userId)
      setNotice(t('adminInternalModels.revoked', { email: targetEmail }))
      log({
        event: 'internal-model-entitlement-admin:revoke-succeeded',
        feature: 'internal-model-entitlement-admin',
        concern: 'operator-ui',
        target_user_id: userId,
      })
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('adminInternalModels.revokeError'))
    }
  }

  return (
    <div
      className="mx-auto max-w-5xl px-4 py-12 sm:px-6"
      data-testid="admin-internal-models-page"
    >
      <AdminToolNav />

      <header className="mb-8 border-b border-border-subtle pb-8">
        <div className="mb-3 flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <FlaskConical className="h-4 w-4 text-primary" strokeWidth={2} aria-hidden="true" />
          {t('adminInternalModels.eyebrow')}
        </div>
        <h1 className="text-hero font-semibold tracking-tight text-foreground">
          {t('adminInternalModels.title')}
        </h1>
        <p className="mt-2 max-w-2xl text-base leading-relaxed text-muted-foreground">
          {t('adminInternalModels.description')}
        </p>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
        <section className="h-fit rounded-xl border border-border bg-surface-1 p-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface-2 text-primary">
            <UserPlus className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
          </div>
          <h2 className="mt-5 text-lg font-medium text-foreground">
            {t('adminInternalModels.addTitle')}
          </h2>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
            {t('adminInternalModels.addDescription')}
          </p>

          <form className="mt-5 space-y-3" onSubmit={handleGrant}>
            <label className="block text-sm font-medium text-foreground" htmlFor="dogfood-email">
              {t('adminInternalModels.emailLabel')}
            </label>
            <input
              id="dogfood-email"
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@meta.com"
              className="h-9 w-full rounded-lg border border-input-border bg-input-bg px-3 text-base text-foreground outline-none placeholder:text-text-muted focus:border-primary focus:ring-2 focus:ring-primary/20"
              data-testid="internal-model-email"
            />
            <button
              type="submit"
              disabled={grant.isPending || !email.trim()}
              className="flex h-9 w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="grant-internal-model-access"
            >
              {grant.isPending ? (
                <LoaderCircle className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden="true" />
              ) : (
                <UserPlus className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
              )}
              {grant.isPending
                ? t('adminInternalModels.adding')
                : t('adminInternalModels.addAction')}
            </button>
          </form>

          <div className="mt-5 rounded-lg border border-warning/40 bg-warning/10 p-3">
            <div className="flex gap-2">
              <ShieldCheck
                className="mt-0.5 h-4 w-4 shrink-0 text-warning"
                strokeWidth={2}
                aria-hidden="true"
              />
              <p className="text-xs font-medium leading-relaxed text-warning">
                {t('adminInternalModels.policyNote')}
              </p>
            </div>
          </div>
        </section>

        <section className="rounded-xl border border-border bg-surface-1 p-5">
          <div className="flex items-start justify-between gap-4 border-b border-border-subtle pb-4">
            <div>
              <h2 className="text-lg font-medium text-foreground">
                {t('adminInternalModels.activeTitle')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('adminInternalModels.activeDescription')}
              </p>
            </div>
            <span className="shrink-0 rounded-full border border-border bg-surface-2 px-3 py-1 text-xs font-medium text-muted-foreground">
              {t('adminInternalModels.accountCount', { count: accounts.length })}
            </span>
          </div>

          {(error || notice) && (
            <div
              className={`mt-4 rounded-lg border p-3 text-sm ${
                error
                  ? 'border-destructive/40 bg-destructive/10 text-destructive'
                  : 'border-success/40 bg-success/10 text-success'
              }`}
              role={error ? 'alert' : 'status'}
            >
              {error ?? notice}
            </div>
          )}

          {entitlements.isLoading ? (
            <div className="flex min-h-48 items-center justify-center text-muted-foreground">
              <LoaderCircle className="h-5 w-5 animate-spin" strokeWidth={2} aria-hidden="true" />
              <span className="ml-2 text-sm">{t('adminInternalModels.loading')}</span>
            </div>
          ) : entitlements.isError ? (
            <div className="mt-4 rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive" role="alert">
              {errorMessage(entitlements.error, t('adminInternalModels.loadError'))}
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex min-h-48 flex-col items-center justify-center px-6 text-center">
              <FlaskConical className="h-6 w-6 text-text-muted" strokeWidth={1.5} aria-hidden="true" />
              <p className="mt-3 text-sm font-medium text-foreground">
                {t('adminInternalModels.emptyTitle')}
              </p>
              <p className="mt-1 text-xs font-medium text-muted-foreground">
                {t('adminInternalModels.emptyDescription')}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border-subtle" data-testid="internal-model-accounts">
              {accounts.map((account) => {
                const isRemoving = revoke.isPending && revoke.variables === account.user_id
                return (
                  <li key={account.user_id} className="flex items-center gap-4 py-4 first:pt-5 last:pb-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border bg-surface-2 text-xs font-semibold uppercase text-foreground">
                      {account.email.slice(0, 2)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{account.email}</p>
                      <p className="mt-0.5 text-xs font-medium text-muted-foreground">
                        {t('adminInternalModels.accessScope')}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={revoke.isPending}
                      onClick={() => void handleRevoke(account.user_id, account.email)}
                      className="flex h-9 shrink-0 items-center gap-2 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-muted-foreground transition-colors hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
                      aria-label={t('adminInternalModels.removeFor', { email: account.email })}
                    >
                      {isRemoving ? (
                        <LoaderCircle className="h-4 w-4 animate-spin" strokeWidth={2} aria-hidden="true" />
                      ) : (
                        <Trash2 className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
                      )}
                      {t('adminInternalModels.removeAction')}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  )
}
